import React, { useCallback, useEffect, useState } from 'react';
import {
  ForgeDialog,
  ForgeIcon,
  ForgeIconButton,
  ForgeScaffold,
  ForgeTextField,
  ForgeToolbar,
} from '@tylertech/forge-react';
import './index.scss';
import AttachmentSection from './components/Attachment/AttachmentSection';
import Footer from './components/Footer';
import FrequencySelector from '../FrequencySelector';
import List from './components/Preview/Attachment/List';
import PreviewFooter from './components/Preview/Footer';
import RecipientsField from './components/RecipientsField/RecipientsField';
import SummarySection from './components/Summary/SummarySection';
import { FrequencyConfig } from 'common/types/frequencySelector/frequencySelector';
import { I18n } from 'common/i18n';
import { RequiredFieldsStatus, ScheduleSendValues } from './types/scheduleSend';
import { View } from 'common/types/view';
import {
  buildAttachmentsConfigFromSubscription,
  buildRecipientsFromSubscriptions,
  buildScheduledDeliveryFrequencyConfigFromSubscription,
  buildWatchFrequencyConfigFromSubscription,
  getName,
  getSubject,
} from './builder';
import { frequencyConfigDefaulValues, requiredFieldsStatusDefaultValues } from './constants/defaultVal';
import { tablesFromStoryData } from './components/Attachment/helpers';
import { useEscapeKey } from 'common/visualizations/views/agGridReact/customHooks/useEscapeKey';
import { scheduledSendDefaultValues, createDefaultPDFFullReport } from './helpers';
import {
  fetchExistingSubscriptions,
  fetchUsers,
  postScheduleDeliveryRequest,
  updateScheduleDeliveryRequest,
} from './services';
import { withEmptyRequiredField } from './validators';
import InteractiveUser from 'common/types/users/interactiveUser';
import { isEmpty } from 'lodash';
import { IDialogBeforeCloseEventData } from '@tylertech/forge';
import ErrorInlineMessage from './components/ErrorInlineMessage';
import { ErrorProvider, useError } from './context/errorContext';
import { ScheduledSendError } from './errorHandler';

const scope = 'shared.components.schedule_send_report.modal';

type ModalMode = 'create' | 'preview' | 'edit' | 'update';
type ScheduleSendReportProps = {
  view: View;
  onDismiss: () => void;
  scheduledSendModalVisible: boolean;
  setScheduledSendModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  currentUser: InteractiveUser;
};

const useModalMode = (initialMode: ModalMode = 'create') => {
  const [currentMode, setCurrentMode] = useState<ModalMode>(initialMode);
  const isReadOnlyMode = currentMode === 'preview';

  return { currentMode, setCurrentMode, isReadOnlyMode };
};

const ScheduleSendReport = ({
  onDismiss,
  scheduledSendModalVisible,
  setScheduledSendModalVisible,
  view,
  currentUser,
}: ScheduleSendReportProps) => {
  const defaultValues = scheduledSendDefaultValues(view);
  const [scheduleSendValues, setScheduleSendValues] = useState<ScheduleSendValues>(defaultValues);
  const [frequencyConfig, setFrequencyConfig] = useState<FrequencyConfig>(frequencyConfigDefaulValues);
  const { currentMode, setCurrentMode, isReadOnlyMode } = useModalMode('create');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [requiredFieldsStatus, setRequiredFieldsStatus] = useState<RequiredFieldsStatus>(
    requiredFieldsStatusDefaultValues,
  );
  const { error, setError } = useError();

  /**
   * Updates a specific key in the ScheduleSendValues state.
   *
   * @template K - A key of ScheduleSendValues: 'name', 'subject', 'recipients', 'attachmentConfigs', or 'frequencyConfig'.
   * @param key - The specific field to update in ScheduleSendValues.
   *   - `'name'`: string
   *   - `'subject'`: string
   *   - `'recipients'`: string[]
   *   - `'attachmentConfigs'`: AttachmentConfig[]
   *   - `'frequencyConfig'`: FrequencyConfig
   * @param value - The new value for the specified key, matching its expected type.
   *
   * @example
   * updateScheduleSendValue('name', 'My Schedule');
   * updateScheduleSendValue('recipients', ['user@example.com']);
   */
  const updateScheduleSendValue = useCallback(
    <K extends keyof ScheduleSendValues>(key: K, value: ScheduleSendValues[K]) => {
      setScheduleSendValues((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    [],
  );

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);

    const initializeModal = async () => {
      try {
        const subscriptions = await fetchExistingSubscriptions();
        if (!isMounted) {
          return;
        }
        if (subscriptions.length > 0) {
          const subscriptionInfo = subscriptions[0];

          //Check whether it is a configuration from watch or scheduled delivery
          const isScheduleDeliverySubscription = subscriptionInfo.type === 'scheduled_delivery';

          const name = getName(subscriptionInfo);
          const subject = getSubject(subscriptionInfo);
          const recipients = await fetchUsers(view);
          const recipientsMap = buildRecipientsFromSubscriptions(subscriptions, recipients);
          const attachmentConfigs = isScheduleDeliverySubscription
            ? buildAttachmentsConfigFromSubscription(subscriptionInfo)
            : [createDefaultPDFFullReport()];
          const frequencyConfigValues = isScheduleDeliverySubscription
            ? buildScheduledDeliveryFrequencyConfigFromSubscription(subscriptionInfo)
            : buildWatchFrequencyConfigFromSubscription(subscriptionInfo);
          setCurrentMode('preview');
          setFrequencyConfig(frequencyConfigValues);
          setScheduleSendValues({
            name,
            subject,
            recipients: recipientsMap,
            attachmentConfigs,
            frequencyConfig: frequencyConfigValues,
          });
        } else {
          setCurrentMode('create');
          setScheduleSendValues(scheduledSendDefaultValues(view));
        }
      } catch (err) {
        if (isMounted) {
          console.error('Error loading subscriptions:', err);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    initializeModal();

    return () => {
      isMounted = false;
    };
  }, [view]);

  useEffect(() => {
    updateScheduleSendValue('frequencyConfig', frequencyConfig);
  }, [frequencyConfig]);

  // If a user clicks the esc button, we are closing the modal
  useEscapeKey(onDismiss, scheduledSendModalVisible, window);

  const onSaveConfiguration = async () => {
    if (
      !withEmptyRequiredField({
        scheduleSendValues,
        setRequiredFieldsStatus,
      })
    ) {
      const response = await postScheduleDeliveryRequest(
        scheduleSendValues,
        currentUser,
        setScheduledSendModalVisible,
      );
      if (response?.error) {
        handleSetErrorActionType({
          action: 'creating',
          errorKey: response?.errorKey,
          statusCode: response?.statusCode,
        });
      }
    }
  };

  const onEdit = async () => {
    setCurrentMode('edit');
    updateScheduleSendValue('name', !isEmpty(scheduleSendValues.name) ? scheduleSendValues.name : view.name);
    updateScheduleSendValue(
      'subject',
      !isEmpty(scheduleSendValues.subject) ? scheduleSendValues.subject : view.name,
    );
  };

  const onUpdate = async () => {
    if (
      !withEmptyRequiredField({
        scheduleSendValues,
        setRequiredFieldsStatus,
      })
    ) {
      const response = await updateScheduleDeliveryRequest(
        scheduleSendValues,
        currentUser,
        setScheduledSendModalVisible,
      );
      if (response?.error) {
        // @TODO: Improve error inline message for update error
        handleSetErrorActionType({
          action: 'updating',
          errorKey: response?.errorKey,
          statusCode: response?.statusCode,
        });
      }
    }
  };

  const handleSetErrorActionType = (errorActionType: ScheduledSendError) => {
    setError(errorActionType);
  };

  const textFieldAttrs = {
    className: isReadOnlyMode ? 'read-only' : '',
    disabled: isReadOnlyMode,
    floatLabel: isReadOnlyMode,
    labelPosition: isReadOnlyMode ? 'inset' : 'block-start',
    required: !isReadOnlyMode,
    variant: isReadOnlyMode ? 'plain' : 'outlined',
  };

  return (
    <ForgeDialog
      open={scheduledSendModalVisible}
      on-forge-dialog-close={onDismiss}
      persistent
      data-testid="schedule-send-dialog"
      aria-labelledby="schedule-send-modal-title"
      on-forge-dialog-before-close={(e: CustomEvent<IDialogBeforeCloseEventData>) => {
        if (e.detail.reason === 'backdrop') {
          e.preventDefault();
        }
      }}
    >
      {isLoading ? null : (
        <ForgeScaffold className="schedule-send-modal">
          <div slot="header">
            <ForgeToolbar className="dialog-header tyl-typography">
              <h1
                slot="start"
                className="forge-typography--heading4"
                id="schedule-send-modal-title"
                data-testid="schedule-send-modal-title"
              >
                {I18n.t('title', { scope })}
              </h1>
              <ForgeIconButton densityLevel={3} slot="end">
                <ForgeIcon name="close" onClick={onDismiss} />
              </ForgeIconButton>
            </ForgeToolbar>
            {error && <ErrorInlineMessage />}
          </div>

          <div
            slot="body"
            className={`schedule-send-modal-body ${isReadOnlyMode ? 'preview-mode' : ''}`}
            data-testid="schedule-send-modal-body"
          >
            <ForgeTextField {...textFieldAttrs} invalid={!scheduleSendValues.name}>
              <label htmlFor="schedule-name-field">{I18n.t('schedule_name_label', { scope })}</label>
              <input
                id="schedule-name-field"
                data-testid="schedule-name-field"
                type="text"
                value={scheduleSendValues.name ?? ''}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  updateScheduleSendValue('name', event.target.value);
                }}
                readOnly={isReadOnlyMode}
              />
              {!scheduleSendValues.name && !isReadOnlyMode && (
                <span slot="support-text">{I18n.t('required_field', { scope })}</span>
              )}
            </ForgeTextField>
            <ForgeTextField {...textFieldAttrs} invalid={!scheduleSendValues.subject}>
              <label htmlFor="email-subject-field">{I18n.t('email_subject_label', { scope })}</label>
              <input
                id="email-subject-field"
                data-testid="email-subject-field"
                type="text"
                value={scheduleSendValues.subject ?? ''}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  updateScheduleSendValue('subject', event.target.value);
                }}
                readOnly={isReadOnlyMode}
              />
              {!scheduleSendValues.subject && !isReadOnlyMode && (
                <span slot="support-text">{I18n.t('required_field', { scope })}</span>
              )}
            </ForgeTextField>

            <RecipientsField
              key={`recipients-${view.id}`}
              view={view}
              scheduledSendModalVisible={scheduledSendModalVisible}
              recipients={scheduleSendValues.recipients}
              updateScheduleSendValue={updateScheduleSendValue}
              readOnly={isReadOnlyMode}
              isRecipientEmptyUponSaving={requiredFieldsStatus.isRecipientEmptyUponSaving}
            />

            {isReadOnlyMode ? (
              <List attachments={scheduleSendValues.attachmentConfigs} />
            ) : (
              <>
                <AttachmentSection
                  tableList={tablesFromStoryData(view)}
                  attachmentConfigs={scheduleSendValues.attachmentConfigs}
                  updateScheduleSendValue={updateScheduleSendValue}
                />
                <FrequencySelector
                  value={frequencyConfig}
                  setValue={setFrequencyConfig}
                  className="container-border"
                  isFrequencyTimeInvalid={requiredFieldsStatus.isFrequencyTimeInvalid}
                  isFrequencyStartDateInvalid={requiredFieldsStatus.isFrequencyStartDateInvalid}
                  isFrequencyEndDateInvalid={requiredFieldsStatus.isFrequencyEndDateInvalid}
                  isIntervalInvalid={requiredFieldsStatus.isIntervalInvalid}
                />
              </>
            )}
            <SummarySection scheduleSendValues={scheduleSendValues} />
          </div>

          <div slot="footer" className="schedule-send-footer">
            {isReadOnlyMode ? (
              <PreviewFooter onDismiss={onDismiss} onEdit={onEdit} />
            ) : (
              <Footer
                onDismiss={onDismiss}
                onSaveConfiguration={onSaveConfiguration}
                onUpdate={onUpdate}
                currentMode={currentMode}
              />
            )}
          </div>
        </ForgeScaffold>
      )}
    </ForgeDialog>
  );
};

/*
  Wrap ScheduleSendReport with ErrorProvider so that all components inside of ScheduleSendReport (including subchilds like Footer, Preview/Footer)
  would get access to the error states and setStates of Error Context through `useError()` hook.
 */
export function ScheduleSendReportModal(props: ScheduleSendReportProps) {
  return (
    <ErrorProvider>
      <ScheduleSendReport {...props}></ScheduleSendReport>
    </ErrorProvider>
  );
}
